/**
 * Service d'upload direct vers Bunny CDN avec compression d'images
 * - Convertit automatiquement en WebP
 * - Génère plusieurs tailles (128, 256, 512, 1024)
 * - Objectif : passer de 5 MB → 50-200 KB
 */

// Configuration Bunny CDN
const BUNNY_STORAGE_ZONE = 'vinisyss-audio';
const BUNNY_STORAGE_KEY = 'ee4b88c9-9282-47d0-bd5e233f038b-bed3-41d9';
const BUNNY_STORAGE_HOST = 'https://storage.bunnycdn.com';
const BUNNY_CDN_BASE = 'https://vinisyss-audio.b-cdn.net';

// Tailles d'images à générer
const IMAGE_SIZES = {
  thumbnail: 128,   // Pour les petites listes
  small: 256,       // Pour les grilles
  medium: 512,      // Pour les pages détail
  large: 1024       // Pour les héros/bannières
};

// Qualité WebP (0-1)
const WEBP_QUALITY = 0.82;

/**
 * Génère un UUID v4
 */
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Obtient l'extension depuis le type MIME
 */
function getExtFromMimetype(mimetype) {
  const mimeMap = {
    'audio/mpeg': 'mp3',
    'audio/mp3': 'mp3',
    'audio/wav': 'wav',
    'audio/ogg': 'ogg',
    'audio/aac': 'aac',
    'audio/m4a': 'm4a',
    'audio/x-m4a': 'm4a',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp'
  };
  return mimeMap[mimetype] || 'bin';
}

/**
 * Redimensionne et compresse une image en WebP
 * @param {File} file - Le fichier image original
 * @param {number} maxSize - Taille max (largeur et hauteur)
 * @param {number} quality - Qualité WebP (0-1)
 * @returns {Promise<Blob>} - Le blob WebP compressé
 */
async function compressImage(file, maxSize, quality = WEBP_QUALITY) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      // Calculer les nouvelles dimensions en gardant le ratio
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > maxSize) {
          height = Math.round(height * maxSize / width);
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = Math.round(width * maxSize / height);
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Dessiner l'image redimensionnée
      ctx.drawImage(img, 0, 0, width, height);

      // Convertir en WebP
      canvas.toBlob(
        (blob) => {
          if (blob) {
            console.log(`📐 Compressed: ${file.name} → ${maxSize}px = ${(blob.size / 1024).toFixed(1)} KB`);
            resolve(blob);
          } else {
            reject(new Error('Failed to compress image'));
          }
        },
        'image/webp',
        quality
      );
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Upload un blob vers Bunny CDN
 */
async function uploadBlobToBunny(blob, storagePath, onProgress = null) {
  const uploadUrl = `${BUNNY_STORAGE_HOST}/${BUNNY_STORAGE_ZONE}/${storagePath}`;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const percentComplete = Math.round((event.loaded / event.total) * 100);
        onProgress(percentComplete);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const cdnUrl = `${BUNNY_CDN_BASE}/${storagePath}`;
        resolve(cdnUrl);
      } else {
        reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
    xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('AccessKey', BUNNY_STORAGE_KEY);
    xhr.setRequestHeader('Content-Type', 'image/webp');
    xhr.send(blob);
  });
}

/**
 * Upload un fichier directement vers Bunny CDN (sans compression - pour audio)
 */
export async function uploadToBunnyCDN(file, folder, onProgress = null) {
  const uniqueId = generateUUID();
  const ext = getExtFromMimetype(file.type) || file.name.split('.').pop();
  const storagePath = `${folder}/${uniqueId}.${ext}`;
  const uploadUrl = `${BUNNY_STORAGE_HOST}/${BUNNY_STORAGE_ZONE}/${storagePath}`;

  console.log('📤 uploadToBunnyCDN:', {
    file: file.name,
    type: file.type,
    size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
    folder,
    storagePath
  });

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const percentComplete = Math.round((event.loaded / event.total) * 100);
        onProgress(percentComplete);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const cdnUrl = `${BUNNY_CDN_BASE}/${storagePath}`;
        console.log(`✅ Uploaded to Bunny CDN: ${cdnUrl}`);
        resolve(cdnUrl);
      } else {
        console.error(`❌ Upload failed: ${xhr.status} ${xhr.statusText}`);
        reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
    xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('AccessKey', BUNNY_STORAGE_KEY);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.send(file);
  });
}

/**
 * Upload une image avec compression et génération de plusieurs tailles
 * @param {File} file - Le fichier image original
 * @param {string} folder - Le dossier de destination
 * @param {function} onProgress - Callback de progression
 * @returns {Promise<Object>} - Objet avec les URLs de chaque taille
 */
export async function uploadImageWithSizes(file, folder, onProgress = null) {
  const uniqueId = generateUUID();
  const originalSize = file.size;
  
  console.log(`🖼️ Processing image: ${file.name} (${(originalSize / 1024 / 1024).toFixed(2)} MB)`);

  const urls = {};
  const sizes = Object.entries(IMAGE_SIZES);
  let totalProgress = 0;

  for (let i = 0; i < sizes.length; i++) {
    const [sizeName, maxSize] = sizes[i];
    
    try {
      // Compresser l'image
      const compressedBlob = await compressImage(file, maxSize);
      
      // Upload vers Bunny
      const storagePath = `${folder}/${uniqueId}_${sizeName}.webp`;
      const url = await uploadBlobToBunny(compressedBlob, storagePath, (progress) => {
        const baseProgress = (i / sizes.length) * 100;
        const sizeProgress = (progress / sizes.length);
        if (onProgress) onProgress(Math.round(baseProgress + sizeProgress));
      });

      urls[sizeName] = url;
      console.log(`✅ ${sizeName} (${maxSize}px): ${url} - ${(compressedBlob.size / 1024).toFixed(1)} KB`);
    } catch (error) {
      console.error(`❌ Failed to process ${sizeName}:`, error);
    }

    totalProgress = ((i + 1) / sizes.length) * 100;
    if (onProgress) onProgress(Math.round(totalProgress));
  }

  // Calculer la réduction de taille
  const totalNewSize = Object.keys(urls).length * 100 * 1024; // Estimation
  const reduction = ((originalSize - totalNewSize) / originalSize * 100).toFixed(1);
  console.log(`📊 Size reduction: ~${reduction}% (${(originalSize / 1024 / 1024).toFixed(2)} MB → ~${Object.keys(urls).length * 100} KB total)`);

  // Retourner l'URL de la taille "medium" comme URL principale
  // Plus les URLs de toutes les tailles pour usage responsive
  return {
    url: urls.medium || urls.small || urls.large,
    sizes: urls,
    id: uniqueId
  };
}

/**
 * Upload une image compressée (taille unique - 512px par défaut)
 * Version simplifiée pour rétrocompatibilité
 */
export async function uploadCompressedImage(file, folder, onProgress = null, maxSize = 512) {
  const uniqueId = generateUUID();
  
  console.log(`🖼️ Compressing image: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB) → ${maxSize}px WebP`);

  try {
    // Compresser l'image
    const compressedBlob = await compressImage(file, maxSize);
    
    // Upload vers Bunny
    const storagePath = `${folder}/${uniqueId}.webp`;
    const url = await uploadBlobToBunny(compressedBlob, storagePath, onProgress);

    const reduction = ((file.size - compressedBlob.size) / file.size * 100).toFixed(1);
    console.log(`✅ Compressed & uploaded: ${(file.size / 1024 / 1024).toFixed(2)} MB → ${(compressedBlob.size / 1024).toFixed(1)} KB (-${reduction}%)`);
    console.log(`✅ URL: ${url}`);

    return url;
  } catch (error) {
    console.error('❌ Compression/upload failed:', error);
    throw error;
  }
}

/**
 * Upload un fichier audio vers Bunny CDN (sans compression)
 */
export async function uploadAudioToBunny(file, onProgress = null) {
  return uploadToBunnyCDN(file, 'tracks', onProgress);
}

/**
 * Upload une image de couverture de track (compressée à 512px)
 */
export async function uploadCoverToBunny(file, onProgress = null) {
  return uploadCompressedImage(file, 'covers', onProgress, 512);
}

/**
 * Upload une photo de profil d'artiste (compressée à 512px)
 */
export async function uploadArtistProfileToBunny(file, onProgress = null) {
  return uploadCompressedImage(file, 'artists/profile', onProgress, 512);
}

/**
 * Upload une image de couverture d'artiste (compressée à 1024px pour les bannières)
 */
export async function uploadArtistCoverToBunny(file, onProgress = null) {
  return uploadCompressedImage(file, 'artists/cover', onProgress, 1024);
}

/**
 * Helper pour obtenir l'URL optimale selon le contexte
 * @param {Object|string} imageData - L'objet retourné par uploadImageWithSizes ou une URL simple
 * @param {string} size - La taille souhaitée ('thumbnail', 'small', 'medium', 'large')
 */
export function getOptimalImageUrl(imageData, size = 'medium') {
  if (!imageData) return null;
  if (typeof imageData === 'string') return imageData;
  if (imageData.sizes && imageData.sizes[size]) return imageData.sizes[size];
  return imageData.url || imageData;
}
